/**
 * Exemplos ilustrativos do corpo (envelope) enviado no POST de um webhook.
 * Mantido num único lugar para o preview no diálogo de edição e a documentação
 * completa da tela usarem exatamente os mesmos dados de exemplo.
 *
 * Formato real enviado pelo backend (WebhookDeliveryListener.montarPayload):
 * { evento, enviadoEm, conta, dados }, onde "dados" varia por evento.
 */

export const ENVELOPE_EXEMPLO_ENVIADO_EM = '2026-07-23T14:32:10.123-03:00'
export const ENVELOPE_EXEMPLO_CONTA = '3f2504e0-4f89-11d3-9a0c-0305e82c3301'

export const DESCRICAO_EVENTO_TESTE =
	'Enviado pelo botão "Enviar teste" da tela de edição. É sempre entregue ao endpoint selecionado, independente dos eventos assinados.'

export const EXEMPLOS_DE_DADOS_POR_EVENTO: Record<string, Record<string, any>> = {
	'venda.criada': {
		vendaId: '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d',
		identificador: 'VD-000123',
		tipoDeTransacao: 'VENDA',
		situacao: 'CONCLUIDA',
	},
	'venda.cancelada': {
		vendaId: '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d',
		identificador: 'VD-000123',
		tipoDeTransacao: 'VENDA',
		situacao: 'CANCELADA',
	},
	'pedido.criado': {
		vendaId: '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d',
		identificador: 'PD-000045',
		tipoDeTransacao: 'VENDA',
		situacaoPedido: 'PENDENTE',
		situacaoVenda: 'CONCLUIDA',
	},
	'pedido.atualizado': {
		identificador: 'PD-000045',
		situacaoPedido: 'DESPACHADO',
	},
	'pedido.cancelado': {
		identificador: 'PD-000045',
		situacaoPedido: 'CANCELADO',
	},
	'nota.emitida': {
		vendaId: '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d',
		identificador: 'NT-000078',
		tipoDeTransacao: 'TRANSFERENCIA',
		tipoNota: 'Saída',
		modelo: 'NFE',
		chave: '35260714200166000187550010000000151000000158',
		cStat: '100',
	},
	'nota.cancelada': {
		vendaId: '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d',
		identificador: 'NT-000078',
		tipoDeTransacao: 'TRANSFERENCIA',
		tipoNota: 'Saída',
		modelo: 'NFE',
		chave: '35260714200166000187550010000000151000000158',
		cStat: '101',
	},
	'pagamento.confirmado': {
		linkPagamento: 'https://www.asaas.com/i/abc123',
		identificador: 'VD-000123',
		status: 'PAGO',
	},
	'pagamento.estornado': {
		linkPagamento: 'https://www.asaas.com/i/abc123',
		identificador: 'VD-000123',
		status: 'ESTORNADO',
	},
	'integracao.venda': {
		tipo: 'VENDA',
		nome: 'emitirTransferencia',
		status: 'EXECUTED',
		numeroTicket: 583921,
		identificador: 'VD-000123',
		erro: null,
	},
	'integracao.produto': {
		tipo: 'PRODUTO',
		nome: 'batchCreate',
		status: 'EXECUTED',
		numeroTicket: 583922,
		identificador: null,
		erro: null,
	},
	'integracao.estoque': {
		tipo: 'ESTOQUE',
		nome: 'adicionar',
		status: 'EXECUTED',
		numeroTicket: 583923,
		identificador: null,
		erro: null,
	},
	'integracao.tabela_de_preco': {
		tipo: 'TABELA_DE_PRECO',
		nome: 'update',
		status: 'EXECUTED',
		numeroTicket: 583924,
		identificador: null,
		erro: null,
	},
	'integracao.titulo': {
		tipo: 'TITULO',
		nome: 'update',
		status: 'EXECUTED',
		numeroTicket: 583925,
		identificador: null,
		erro: null,
	},
	'movimentacao_estoque': {
		loja: {
			cnpj: '12345678000199',
		},
		produto: {
			sku: 'CAM-AZ-M',
			ean: '7891234567890',
		},
		movimentacao: {
			tipo: 'estoque',
			quantidade: -3,
			origem: 'venda',
		},
	},
	'webhook.teste': {
		mensagem: 'Evento de teste do Almode',
		webhookId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
	},
}

export interface DocDeEvento {
	/** Categoria de origem, ex.: "Integração (API)", "Ação no sistema", "Gateway de pagamento". */
	origem: string
	/** Explicação de quando e por quem o evento é disparado. */
	quandoDispara: string
}

export const DOC_POR_EVENTO: Record<string, DocDeEvento> = {
	'integracao.venda': {
		origem: 'Integração (API)',
		quandoDispara:
			'Disparado quando uma emissão de venda ou nota fiscal solicitada pela API de integração termina de ser processada. Funciona como o retorno assíncrono dessa operação (traz o status final e o identificador da venda), no lugar de você ficar consultando o resultado pelo número do ticket.',
	},
	'integracao.produto': {
		origem: 'Integração (API)',
		quandoDispara:
			'Disparado quando uma criação ou edição de produtos em lote solicitada pela API de integração termina de ser processada.',
	},
	'integracao.estoque': {
		origem: 'Integração (API)',
		quandoDispara:
			'Disparado quando um ajuste de estoque (entrada ou saída) solicitado pela API de integração termina de ser processado.',
	},
	'integracao.tabela_de_preco': {
		origem: 'Integração (API)',
		quandoDispara:
			'Disparado quando uma atualização de tabela de preço solicitada pela API de integração termina de ser processada.',
	},
	'integracao.titulo': {
		origem: 'Integração (API)',
		quandoDispara:
			'Disparado quando um envio de títulos solicitado pela API de integração termina de ser processado.',
	},
	'venda.criada': {
		origem: 'Ação no sistema',
		quandoDispara:
			'Disparado quando uma venda do tipo "Venda" é concluída num PDV e NÃO é um pedido (venda de balcão). Pedidos e notas têm eventos próprios (pedido.* e nota.emitida).',
	},
	'venda.cancelada': {
		origem: 'Ação no sistema',
		quandoDispara:
			'Disparado quando uma venda de balcão (tipo "Venda", com PDV, sem pedido) é cancelada.',
	},
	'pedido.criado': {
		origem: 'Ação no sistema',
		quandoDispara:
			'Disparado quando uma venda que tem pedido de logística é criada. Traz a situação inicial do pedido (normalmente PENDENTE) e a situação da venda.',
	},
	'pedido.atualizado': {
		origem: 'Ação no sistema',
		quandoDispara:
			'Disparado quando a situação de um pedido muda (por exemplo, via PATCH /public/vendas/{identificador}/pedidos). Traz o identificador e a nova situacaoPedido.',
	},
	'pedido.cancelado': {
		origem: 'Ação no sistema',
		quandoDispara:
			'Disparado quando um pedido é cancelado, seja pelo cancelamento da venda seja pela mudança de situação para CANCELADO. Traz o identificador e a situacaoPedido.',
	},
	'nota.emitida': {
		origem: 'Ação no sistema',
		quandoDispara:
			'Disparado quando uma nota é emitida, por exemplo nota de transferência, nota de devolução ou nota gerada por integração. Traz o tipo da nota (Saída/Entrada) e o tipo de transação; quando a nota já foi emitida, inclui o modelo (NFE/NFCE), a chave e o cStat.',
	},
	'nota.cancelada': {
		origem: 'Ação no sistema',
		quandoDispara:
			'Disparado quando uma nota (transferência, devolução ou gerada por integração) é cancelada. Traz os mesmos dados de nota.emitida.',
	},
	'pagamento.confirmado': {
		origem: 'Gateway de pagamento',
		quandoDispara:
			'Disparado quando um pagamento por link é confirmado. Quem aciona é o gateway de pagamento (Asaas), avisando o Almode de que o cliente efetuou o pagamento.',
	},
	'pagamento.estornado': {
		origem: 'Gateway de pagamento',
		quandoDispara:
			'Disparado quando um pagamento por link é estornado, também a partir do aviso do gateway de pagamento (Asaas).',
	},
	'movimentacao_estoque': {
		origem: 'Ação no sistema',
		quandoDispara:
			'Disparado a cada movimentação de quantidade em estoque ou de quantidade empenhada (venda, nota de entrada, ajuste manual, transferência, cancelamento, reserva etc.), exceto movimentações de inventário. Traz o CNPJ da loja, o SKU e o EAN do produto, e a quantidade movimentada (positiva para entrada, negativa para saída). Se a mesma operação alterar estoque e empenho ao mesmo tempo, dispara um evento para cada um.',
	},
	'webhook.teste': {
		origem: 'Teste manual',
		quandoDispara: DESCRICAO_EVENTO_TESTE,
	},
}

export function docDoEvento(chaveDoEvento: string): DocDeEvento | null {
	return DOC_POR_EVENTO[chaveDoEvento] || null
}

export function montarEnvelopeDeExemplo(chaveDoEvento: string) {
	return {
		evento: chaveDoEvento,
		enviadoEm: ENVELOPE_EXEMPLO_ENVIADO_EM,
		conta: ENVELOPE_EXEMPLO_CONTA,
		dados: EXEMPLOS_DE_DADOS_POR_EVENTO[chaveDoEvento] || {},
	}
}

export function montarEnvelopeDeExemploFormatado(chaveDoEvento: string): string {
	return JSON.stringify(montarEnvelopeDeExemplo(chaveDoEvento), null, 2)
}
